import React, { useState, useRef, useEffect } from 'react';
import './askProAI.css';
import ApiService from '../../services/apiService';

// Slide-in chat panel for the "Ask Pro360 AI" button on the Property Dashboard.
// Calls AI_ASK_DASHBOARD (see endPoint.js) - backend contract:
//   POST { messages: [{ role: string, content: string }] }
//   -> { status: bool, data: { assistantMessage: string, toolCalls: [], charts: [] }, message: string|null }
const SparkleIcon = ({ color = '#fff', size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2z" />
        <path d="M19 14l.9 2.6L22.5 17.5l-2.6.9L19 21l-.9-2.6-2.6-.9 2.6-.9L19 14z" opacity="0.7" />
    </svg>
);

const SendIcon = ({ color = '#fff', size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
        <line x1="22" y1="2" x2="11" y2="13"></line>
        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
    </svg>
);

// Confirmed live response shape: { status, data: { assistantMessage, toolCalls, charts }, message }
const extractAnswer = (data) => {
    if (!data) return null;
    if (data.status === false) return data.message || null;
    return data.data?.assistantMessage || null;
};

// assistantMessage comes back with light markdown (**bold**) - render just
// that plus line breaks as React nodes rather than pulling in a markdown lib.
const renderMessageText = (text) =>
    text.split('\n').map((line, lineIdx) => (
        <React.Fragment key={lineIdx}>
            {lineIdx > 0 && <br />}
            {line.split(/(\*\*[^*]+\*\*)/g).map((part, partIdx) =>
                part.startsWith('**') && part.endsWith('**') ? <strong key={partIdx}>{part.slice(2, -2)}</strong> : part
            )}
        </React.Fragment>
    ));

export const AskProAIButton = ({ onClick }) => (
    <button className="ask-ai-btn" onClick={onClick}>
        <SparkleIcon />
        <span>Ask Pro360 AI</span>
    </button>
);

const AskProAI = ({ isOpen, onClose, context = 'PropertyDashboard' }) => {
    const apiservice = new ApiService();
    const [messages, setMessages] = useState([
        { role: 'ai', text: "Hi, I'm Pro360 AI. Ask me anything about this dashboard - e.g. \"What is the total invoices for each customer?\"" },
    ]);
    const [question, setQuestion] = useState('');
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, sending]);

    const handleSend = async () => {
        const trimmed = question.trim();
        if (!trimmed || sending) return;
        const nextMessages = [...messages, { role: 'user', text: trimmed }];
        setMessages(nextMessages);
        setQuestion('');
        setSending(true);
        try {
            const chatMessages = [
                { role: 'system', content: `Dashboard context: ${context}` },
                ...nextMessages.map((msg) => ({ role: msg.role === 'ai' ? 'assistant' : 'user', content: msg.text })),
            ];
            const res = await apiservice.post('AI_ASK_DASHBOARD', { messages: chatMessages });
            const answer = extractAnswer(res?.data) || "Sorry, I couldn't find an answer to that.";
            setMessages((prev) => [...prev, { role: 'ai', text: answer }]);
        } catch (error) {
            setMessages((prev) => [...prev, { role: 'ai', text: 'Something went wrong while reaching Pro360 AI. Please try again.' }]);
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="ask-ai-overlay" onClick={onClose}></div>
            <div className="ask-ai-panel">
                <div className="ask-ai-header">
                    <div className="ask-ai-header-title">
                        <SparkleIcon color="#8B5CF6" size={20} />
                        <span>Ask Pro360 AI</span>
                    </div>
                    <button className="ask-ai-close" onClick={onClose}>&times;</button>
                </div>
                <div className="ask-ai-messages" ref={scrollRef}>
                    {messages.map((msg, idx) => (
                        <div key={idx} className={`ask-ai-message ${msg.role}`}>
                            {renderMessageText(msg.text)}
                        </div>
                    ))}
                    {sending && (
                        <div className="ask-ai-message ai ask-ai-typing">
                            <span></span><span></span><span></span>
                        </div>
                    )}
                </div>
                <div className="ask-ai-input-row">
                    <textarea
                        className="ask-ai-input"
                        placeholder="Ask about this dashboard's data..."
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={1}
                    />
                    <button className="ask-ai-send" onClick={handleSend} disabled={sending || !question.trim()}>
                        <SendIcon />
                    </button>
                </div>
            </div>
        </>
    );
};

export default AskProAI;
